import { ToDoListId } from '@app/data-model/list/to-do-list';
/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import * as _ from 'underscore';
import { Logger } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { ExamCacheService } from '@app/ui/client-cache/exam-cache.service';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { MessageService } from '@core/lib/services/message.service';
import { Events, EventService } from '@app/common/event';

const log = new Logger('TabWorkListService');

@Injectable({
  providedIn: 'root'
})
export class TabWorkListService {
  constructor(
    private appConfigService: AppConfigService,
    private examCacheService: ExamCacheService,
    private dataService: ProxyDataService,
    private messageService: MessageService,
    private eventService: EventService
  ) {
    this.afterEvents();
  }

  saveExamToTabWorklist(requestedProcedureId) {
    if (requestedProcedureId && this.appConfigService.getAppConfigByKey('unlimitedTabs') === 'true') {
      if (!this.examCacheService.getTabExamCache(requestedProcedureId)) {
        const params = { target: [ToDoListId.TAB] };
        this.dataService
          .update('recent-exams/save', requestedProcedureId, params)
          .then(() => {
            log.info('Save tab to Tab Worklist  ' + requestedProcedureId);
            this.examCacheService.setTabExamCache(requestedProcedureId);

            const activeWorklist = this.appConfigService.getActiveList();
            if (activeWorklist && activeWorklist.id === ToDoListId.TAB) {
              this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
            }
          })
          .catch(error => {
            log.error('Error occurred while saving tab worklist exam, ERROR - ', JSON.stringify(error));
          });
      }
    }
  }

  removeExamFromTabWorklist(requestedProcedureId) {
    if (
      this.appConfigService.getAppConfigByKey('unlimitedTabs') === 'true' &&
      requestedProcedureId &&
      this.examCacheService.getTabExamCache(requestedProcedureId)
    ) {
      const params = { ids: ['' + requestedProcedureId] };
      this.dataService
        .update('recent-exams/tabs/delete', '', params)
        .then(() => {
          log.info('Deleted tab from Tab Worklist  ' + requestedProcedureId);
          this.examCacheService.removeTabExamCache(requestedProcedureId);
          const activeWorklist = this.appConfigService.getActiveList();
          if (activeWorklist && activeWorklist.id === ToDoListId.TAB) {
            this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
          }
        })
        .catch(error => {
          log.error('Error occurred while removing an exam from tab worklist, ERROR - ', JSON.stringify(error));
        });
    }
  }

  removeSelectedExamFromTabWorklist(ids) {
    return new Promise((resolve, reject) => {
      if (this.appConfigService.getAppConfigByKey('unlimitedTabs') === 'true') {
        const params = { ids };
        this.dataService
          .update('recent-exams/tabs/delete', '', params)
          .then(() => {
            log.info('Deleted tabs from Tab Worklist  ' + ids);
            this.examCacheService.removeSelectedTabExamCache(ids);
            const activeWorklist = this.appConfigService.getActiveList();
            if (activeWorklist && activeWorklist.id === ToDoListId.TAB) {
              this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
            }
            resolve(undefined);
          })
          .catch(error => {
            log.error('Error occurred while removing an exam from tab worklist, ERROR - ', JSON.stringify(error));
            reject();
          });
      } else {
        resolve(undefined);
      }
    });
  }

  private removeAllExamFromTabWorklist() {
    if (this.appConfigService.getAppConfigByKey('unlimitedTabs') === 'true') {
      this.dataService
        .update('recent-exams/tabs/delete', undefined)
        .then(() => {
          log.info('Deleted tabs from Tab Worklist');
          this.examCacheService.removeAllTabExamCache();
        })
        .catch(error => {
          log.error('Error occurred while removing all exams from tab worklist, ERROR - ', JSON.stringify(error));
        });
      const activeWorklist = this.appConfigService.getActiveList();
      if (activeWorklist && activeWorklist.id === ToDoListId.TAB) {
        this.messageService.broadcast('list.refresh', { worklist: activeWorklist });
      }
    }
  }

  private afterEvents() {
    this.eventService.afterEvent(Events.preview.name, target => {
      //Blocking remote exams for tab worklist
      if (
        !_.isUndefined(target.keys) &&
        target.keys.repositoryType !== undefined &&
        target.keys.repositoryType !== 'Local'
      ) {
        return;
      }
      this.saveExamToTabWorklist(target.id);
    });

    this.eventService.afterEvent(Events.logout.name, () => {
      this.removeAllExamFromTabWorklist();
    });
  }
}
